/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useState } from "react"
import { Button } from "@duxton-web/ui"
import { WarningFilled } from "@duxton-web/icons"
import {
  LocaleContent,
  resolveCopy,
  translateMultipleMap,
  useLocaleContext,
} from "common/utils/i18n"
import { EMPTY } from "common/constants/misc"
import { fetchLocale } from "utils/locale"
import { storageHelper } from "utils/storage"

import styles from "./CrashFallback.modules.css"

/**
 * TEMPORARY — English fallbacks for the crash copy, same pattern as
 * `SessionErrorPage`. The PhraseApp keys are registered in
 * `scripts/phraseapp/phraseapp_config.json`, but until they're published in
 * the remote PhraseApp project and pulled, a translation lookup can return ""
 * (prod) or "CONTENT NOT FOUND" (non-prod) — neither of which is safe to show
 * on a last-resort crash screen.
 *
 * TODO: delete this object and read `strings.*` directly once the copy is
 * published and pulled. Confirm with `yarn check-missing-locales:fresh --strict`.
 */
const CRASH_TEXT_FALLBACK = {
  title: "Something went wrong",
  description:
    "We're sorry, this page ran into an unexpected error. Please reload to continue.",
  reload: "Reload page",
}

/**
 * `critical` (root `MonitorErrorBoundary` in `src/index.jsx`) renders in
 * place of `<App/>` entirely, so it is never a descendant of `HomeContainer`'s
 * `LocaleContext.Provider` (which lives deep inside `App`) — reading it via
 * context would always see the provider's hardcoded default
 * (`locale: "en", localeContent: EMPTY`), never the merchant's real locale.
 * This loads a locale bundle independently via `fetchLocale` — the single
 * shared `public/locales/*` loader also used by `HomeContainer` and
 * `LocaleProvider`; see `utils/locale.ts` for why a second `import()` call
 * site must not be added here — so the root fallback still gets translated
 * once `common.appCrash.*` is published, instead of permanently showing the
 * English fallback. `degraded` (page-level boundary) mounts inside that
 * provider, so it can — and should, to avoid the load/flash — read locale via
 * context like the rest of the app.
 */
const useIndependentLocaleContent = (enabled: boolean): LocaleContent => {
  const [localeContent, setLocaleContent] = useState<LocaleContent>(EMPTY)

  useEffect(() => {
    if (!enabled) {
      return
    }
    fetchLocale(storageHelper.getLang(), (content: LocaleContent) => {
      setLocaleContent(content)
    })
  }, [enabled])

  return localeContent
}

export interface CrashFallbackProps {
  /**
   * `critical`: root `MonitorErrorBoundary` fallback — fixed full-viewport
   * overlay, hard reload is the only recovery since the whole app shell
   * (sidebar/navigation included) is gone.
   * `degraded`: page-level `MonitorErrorBoundary` fallback — replaces only the
   * routed content area, sidebar and app shell keep working.
   */
  variant: "critical" | "degraded"
}

/**
 * Shared crash fallback for both `MonitorErrorBoundary` levels of the BTP Web
 * Frontend Monitoring Standard. See `useIndependentLocaleContent` for why
 * `variant` also selects how locale content is read, not just how it's
 * styled.
 */
export const CrashFallback: FC<CrashFallbackProps> = ({ variant }) => {
  const isCritical = variant === "critical"

  const contextLocaleContent = useLocaleContext("localeContent")
  const independentLocaleContent = useIndependentLocaleContent(isCritical)
  const localeContent = isCritical
    ? independentLocaleContent
    : contextLocaleContent

  const strings = translateMultipleMap(localeContent, {
    title: "common.appCrash.title",
    description: "common.appCrash.description",
    reload: "common.appCrash.reload",
  })

  const handleReload = () => window.location.reload()

  return (
    <div
      className={isCritical ? styles.overlay : styles.container}
      role="alert"
    >
      <div className={styles.content}>
        <WarningFilled
          className={isCritical ? styles.iconCritical : styles.iconDegraded}
        />
        <div
          className={isCritical ? styles.titleCritical : styles.titleDegraded}
        >
          {resolveCopy(strings.title, CRASH_TEXT_FALLBACK.title)}
        </div>
        <div className={styles.description}>
          {resolveCopy(strings.description, CRASH_TEXT_FALLBACK.description)}
        </div>
        <Button
          type="primary"
          className={
            isCritical
              ? styles.reloadButtonCritical
              : styles.reloadButtonDegraded
          }
          onClick={handleReload}
        >
          {resolveCopy(strings.reload, CRASH_TEXT_FALLBACK.reload)}
        </Button>
      </div>
    </div>
  )
}
